import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";

import { ALLIANCE_DAO_REQUEST_ACCESS_SOURCE } from "@/lib/request-access-attribution";

const ALLIANCE_DAO_WORDMARK = `${import.meta.env.BASE_URL}alliance-dao/alliance-wordmark.jpg`;

const WALLET_STEPS = [
  {
    number: "01",
    title: "Request Access",
    description: "Alliance founders get fast-tracked onto the whitelist with a free wallet access.",
  },
  {
    number: "02",
    title: "Import credits or top up",
    description: "One balance. Every provider. Prepay or auto-refill. Your wallet, your rules.",
  },
  {
    number: "03",
    title: "Run tasks across providers",
    description:
      "Spin up GPUs or open source models across our provider network. Pay only for what you use.",
  },
  {
    number: "04",
    title: "Earn on the excess",
    description:
      "Put idle compute back to work, earn and focus your spending on highest quality hardware.",
  },
  {
    number: "05",
    title: "Ship faster",
    description:
      "Iterate on models, train, fine-tune, deploy. From idea to production without the infra tax.",
  },
] as const;

export const Route = createFileRoute("/alliancedao")({
  head: () => ({
    meta: [
      { title: "The Compute Wallet · AllianceDAO × GENRL" },
      {
        name: "description",
        content:
          "Alliance founders can manage compute and model credits, spend, tasks and earnings across providers in one wallet.",
      },
      { property: "og:title", content: "The Compute Wallet · AllianceDAO × GENRL" },
      {
        property: "og:description",
        content:
          "One wallet for compute and model credits across providers, built for Alliance founders.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "preload", as: "image", href: ALLIANCE_DAO_WORDMARK }],
  }),
  component: AllianceDAOAccessPage,
});

function RequestAccessButton() {
  return (
    <Link
      to="/request-access"
      search={{ source: ALLIANCE_DAO_REQUEST_ACCESS_SOURCE }}
      aria-label="Request access to the Compute Wallet"
      className="alliance-dao-access-link group inline-flex min-h-14 items-center justify-center gap-4 rounded-full bg-[#f3f0e8] px-7 py-3 text-[11px] font-semibold text-black transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#f3f0e8] active:translate-y-0 sm:px-9"
    >
      <span>Request access</span>
      <ArrowUpRight
        aria-hidden="true"
        className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 motion-reduce:transition-none"
        strokeWidth={1.75}
      />
    </Link>
  );
}

function AllianceDAOAccessPage() {
  return (
    <main className="alliance-dao-page min-h-[100svh] overflow-hidden bg-black text-[#f3f0e8]">
      <section
        aria-labelledby="alliance-dao-title"
        className="alliance-dao-hero mx-auto flex min-h-[100svh] w-full max-w-7xl flex-col items-center justify-center gap-10 px-6 pb-8 pt-[clamp(5rem,14svh,10rem)] text-center sm:gap-12 sm:px-10 sm:pb-10 lg:px-16"
      >
        <img
          src={ALLIANCE_DAO_WORDMARK}
          width={1280}
          height={144}
          alt="AllianceDAO"
          className="alliance-dao-logo block h-auto w-full max-w-[486px]"
        />
        <p className="text-[9px] font-semibold uppercase tracking-[0.24em] text-white/50 sm:text-[10px]">
          GENRL × ALLIANCEDAO
        </p>
        <h1
          id="alliance-dao-title"
          className="max-w-4xl text-balance font-display text-[clamp(1.528rem,4.389vw,3.658rem)] font-medium leading-[0.88] tracking-[-0.065em]"
        >
          The Compute Wallet
        </h1>
        <p className="max-w-2xl text-pretty text-[13px] leading-[1.6] text-white/68 sm:text-base">
          Alliance founders can now view their compute and model credits, manage spend, run tasks
          across tens of hardware providers worldwide and earn - in one single wallet - without ever
          migrating their setup. Now for free for first 1 year.
        </p>
        <div>
          <RequestAccessButton />
        </div>
      </section>

      <section
        aria-label="What Alliance founders can do with the Compute Wallet"
        className="mx-auto w-full max-w-7xl px-6 sm:px-10 lg:px-16"
      >
        <ol className="border-t border-white/20">
          {WALLET_STEPS.map((step) => (
            <li
              key={step.number}
              className="alliance-dao-step grid gap-5 border-b border-white/20 py-9 sm:grid-cols-[72px_minmax(0,1fr)] sm:gap-x-8 sm:py-11 lg:grid-cols-[88px_minmax(240px,0.75fr)_minmax(0,1fr)] lg:items-start lg:gap-x-12 lg:py-14"
            >
              <span className="font-mono text-[10px] tracking-[0.12em] text-white/50">
                {step.number}
              </span>
              <h2 className="max-w-md text-xl font-medium leading-tight tracking-[-0.025em] sm:text-2xl">
                {step.title}
              </h2>
              <p className="max-w-2xl text-[13px] leading-[1.6] text-white/62 sm:col-start-2 sm:text-sm lg:col-start-3">
                {step.description}
              </p>
            </li>
          ))}
        </ol>
      </section>
    </main>
  );
}
